import { useState, useEffect } from 'react'
import { analyticsApi } from '../services/api'
import type { DateRangeOption } from './useDashboard'

export interface AgentCostSummary {
  agent_name: string
  runs: number
  total_input_tokens: number
  total_output_tokens: number
  total_cached_tokens: number
  total_cost_usd: number
}

export interface TokenTotals {
  total_input_tokens: number
  total_output_tokens: number
  total_cached_tokens: number
  total_cost_usd: number
}

export interface ActivityCounts {
  total_resumes_submitted: number
  total_jobs_submitted: number
  total_interviews: number
}

export interface AgentTableRow {
  agent_name: string
  runs: number
  completed_runs: number
  failed_runs: number
  versions_seen: string | null
  first_run_at: string | null
  last_run_at: string | null
  total_input_tokens: number
  total_output_tokens: number
  total_cached_tokens: number
  total_cost_usd: number
}

export interface WorkflowTableRow {
  workflow_name: string
  runs: number
  completed_runs: number
  failed_runs: number
  total_input_tokens: number
  total_output_tokens: number
  total_cached_tokens: number
  total_cost_usd: number
  first_run_at: string | null
  last_run_at: string | null
}

export interface CostDashboardData {
  date_from: string
  date_to: string
  resume_processing: AgentCostSummary
  job_analysis: AgentCostSummary
  job_matching: AgentCostSummary
  totals: TokenTotals
  activity: ActivityCounts
  agents: AgentTableRow[]
  workflows: WorkflowTableRow[]
}

export interface UseCostDashboardParams {
  dateRange: DateRangeOption
  customStartDate?: Date
  customEndDate?: Date
}

function dateRangeToIso(range: DateRangeOption, customStart?: Date, customEnd?: Date): { from: string; to: string } {
  const now = new Date()
  const toIso = (d: Date) => d.toISOString()

  if (range === 'custom' && customStart && customEnd) {
    return { from: toIso(customStart), to: toIso(customEnd) }
  }

  const to = toIso(now)

  if (range === 'last_7_days') {
    const from = new Date(now)
    from.setDate(from.getDate() - 7)
    from.setHours(0, 0, 0, 0)
    return { from: toIso(from), to }
  }
  if (range === 'last_30_days') {
    const from = new Date(now)
    from.setDate(from.getDate() - 30)
    from.setHours(0, 0, 0, 0)
    return { from: toIso(from), to }
  }
  if (range === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    return { from: toIso(from), to }
  }
  // today or fallback
  const from = new Date(now); from.setHours(0, 0, 0, 0)
  return { from: toIso(from), to }
}

export const useCostDashboard = (params: UseCostDashboardParams) => {
  const [data, setData] = useState<CostDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const { from, to } = dateRangeToIso(params.dateRange, params.customStartDate, params.customEndDate)
      const response = await analyticsApi.getAgentCostDashboard({ from, to })
      setData(response.data)
    } catch (err: any) {
      console.error('Failed to fetch cost dashboard:', err)
      setError(err.response?.data?.detail || 'Failed to load cost dashboard data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [params.dateRange, params.customStartDate, params.customEndDate])

  return { data, loading, error, refetch: fetchData }
}
